import { isFreeEnlargementLine } from "src/utils/free-enlargement";
import type { PickTarget } from "./pending-picks";

/**
 * Sending resumed picks back where they belong.
 *
 * A reload mid-upload used to leave the customer with two rows of the same
 * size: the original group holding what landed (25), and a brand-new group
 * for the photos offered back after the reload (35). Stage order a7a8d408.
 * The earlier load now records the group its upload was filling (see
 * PendingPicks.describe), and the resumed upload goes into that group, or
 * failing that into another group of the same size, before ever creating one.
 */

interface GroupLike {
  id?: string | null;
  isFreeEnlargement?: boolean;
  photoSize?: { id?: string | null } | null;
  photos?: ({ rank?: number | null } | null)[] | null;
}

export interface ResumeGroupChoice {
  id: string;
  /** `original`: the group the picks were picked for; `same-size`: another of that size. */
  reason: "original" | "same-size";
  /** First rank for the new photos: after everything the group holds. */
  firstRank: number;
}

/** One past the highest rank in the group, or 0 when it holds nothing. */
export function nextRank(group: GroupLike): number {
  let max = -1;
  (group.photos ?? []).forEach((photo) => {
    const rank = photo?.rank;
    if (typeof rank === "number" && Number.isFinite(rank) && rank > max) max = rank;
  });
  return max + 1;
}

/**
 * The existing group a resumed upload of size `photoSizeId` should go into, or
 * null to create one as before.
 *
 * @param groups the order's groups as the server returned them moments ago
 *   (never a cache: a group the cache still shows may be gone).
 * @param target where the earlier load said its picks were headed.
 */
export function chooseResumeGroup(
  groups: readonly (GroupLike | null)[] | null | undefined,
  target: PickTarget | null | undefined,
  photoSizeId: string | null | undefined,
): ResumeGroupChoice | null {
  if (!photoSizeId || !Array.isArray(groups)) return null;
  // Never the free enlargement: the backend refuses uploads into it.
  const usable = groups.filter(
    (g): g is GroupLike & { id: string } =>
      !!g &&
      typeof g.id === "string" &&
      g.id.length > 0 &&
      !isFreeEnlargementLine(g) &&
      g.photoSize?.id === photoSizeId,
  );
  const original = target?.orderPhotoId
    ? usable.find((g) => g.id === target.orderPhotoId)
    : undefined;
  if (original) return { id: original.id, reason: "original", firstRank: nextRank(original) };
  // The newest of that size: the one the customer most likely just filled.
  const sameSize = usable[usable.length - 1];
  if (sameSize) return { id: sameSize.id, reason: "same-size", firstRank: nextRank(sameSize) };
  return null;
}

/**
 * The size to preselect for a resume: the one the earlier load recorded, if
 * it is still on offer. Null means "not known" -- the resume then stops at the
 * selection, as before, rather than uploading at a size nobody chose.
 */
export function restoredPhotoSize(
  target: PickTarget | null | undefined,
  options: readonly { id?: string | null }[],
): string | null {
  const id = target?.photoSizeId;
  if (!id) return null;
  return options.some((o) => o.id === id) ? id : null;
}

/** A resume waiting to start its upload once the restored photos are on screen. */
export interface PendingResumeUpload {
  pickIds: ReadonlySet<string>;
  photoSizeId: string;
  target: PickTarget;
}

/**
 * Whether a resumed selection should start uploading now.
 *
 * - `wait`  -- not ready yet (the size has not been applied); look again on the next render.
 * - `start` -- go: the same path as "Potrdi in naloži".
 * - `skip`  -- give up quietly and leave the photos selected, as before:
 *              none of them made it onto the screen, or an upload is running.
 */
export function resumeUploadDecision(input: {
  pending: PendingResumeUpload | null;
  files: readonly { pickId?: string }[];
  photoSizeId: string | null | undefined;
  busy: boolean;
}): "wait" | "start" | "skip" {
  const { pending, files, photoSizeId, busy } = input;
  if (!pending) return "skip";
  if (busy) return "skip";
  if (!files.some((f) => f.pickId && pending.pickIds.has(f.pickId))) return "skip";
  if (photoSizeId !== pending.photoSizeId) return "wait";
  return "start";
}
